/**
 * Essential utilities for MercNet Core v1
 */

// String utilities
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const camelCase = (str: string): string => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, '')
}

// Object utilities
export const pick = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

// Type guards
export const isObject = (item: unknown): item is Record<string, any> => {
  return item !== null && typeof item === 'object' && !Array.isArray(item)
}

export const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

export const isNumber = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value)
}

export const isBoolean = (value: unknown): value is boolean => {
  return typeof value === 'boolean'
}

export const isFunction = (value: unknown): value is Function => {
  return typeof value === 'function'
}

export const isArray = <T>(value: unknown): value is T[] => {
  return Array.isArray(value)
}

// Array utilities
export const unique = <T>(array: T[]): T[] => {
  return Array.from(new Set(array))
}

// Promise utilities
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => globalThis.setTimeout(resolve, ms))
}

export const retry = async <T>(
  fn: () => Promise<T>,
  options: {
    retries: number
    delay?: number
    backoff?: number
  }
): Promise<T> => {
  let lastError: Error

  for (let i = 0; i <= options.retries; i++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error
      if (i < options.retries) {
        const delay = options.delay || 1000
        const backoff = options.backoff || 1
        await sleep(delay * Math.pow(backoff, i))
      }
    }
  }

  throw lastError!
}

// Date utilities
export const formatDate = (date: Date, format: string = 'YYYY-MM-DD'): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

export const isValidDate = (date: unknown): date is Date => {
  return date instanceof Date && !isNaN(date.getTime())
}

// Environment utilities
export const getEnv = (key: string, defaultValue?: string): string => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue || ''
  }
  return defaultValue || ''
}

export const isDevelopment = (): boolean => {
  return getEnv('NODE_ENV') === 'development'
}

export const isProduction = (): boolean => {
  return getEnv('NODE_ENV') === 'production'
}

// Error utilities
export class MercNetError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'MercNetError'
  }
}

export const createError = (
  message: string,
  code?: string,
  details?: Record<string, unknown>
): MercNetError => {
  return new MercNetError(message, code, details)
}
